import * as React from "react";
import { Menu } from "antd";
import { useNavigate, useLocation } from "@opendash/router";
import { Icon } from "@opendash/icons";
import { getTitle, useAppState } from "../state";
import { useTranslation } from "@opendash/i18n";
import Parse from "parse";

const TENANT_ID = "1gcJ4fmP4m";

interface MenuItemData {
  id: string;
  key: string;
  title: string;
  route: string;
  icon: string;
  order: number;
  visible: boolean;
  parentId?: string;
  children?: MenuItemData[];
}

// Hook zum Laden der Menü-Struktur aus Parse
const useMenuItems = () => {
  const [menuItems, setMenuItems] = React.useState<MenuItemData[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadMenuItems = async () => {
      try {
        const MenuItem = Parse.Object.extend("APP_MenuItem");
        const tenant =
          Parse.Object.extend("OD3_Tenant").createWithoutData(TENANT_ID);
        const query = new Parse.Query(MenuItem);
        query.equalTo("tenant", tenant);
        query.equalTo("visible", true);
        query.ascending("order");
        query.limit(200);

        const results = await query.find();

        // Parse-Objekte in MenuItemData konvertieren
        const items: MenuItemData[] = results.map((item) => ({
          id: item.id!,
          key: item.get("key"),
          title: item.get("title"),
          route: item.get("route") || "",
          icon: item.get("icon") || "fa:circle",
          order: item.get("order") || 0,
          visible: true,
          parentId: item.get("parent")?.id,
        }));

        // Hierarchie aufbauen
        const rootItems: MenuItemData[] = [];
        const itemMap = new Map<string, MenuItemData>();

        // Erst alle Items in Map speichern
        for (const item of items) {
          itemMap.set(item.id, { ...item, children: [] });
        }

        // Dann Hierarchie aufbauen
        for (const item of items) {
          const mappedItem = itemMap.get(item.id)!;
          if (item.parentId && itemMap.has(item.parentId)) {
            const parent = itemMap.get(item.parentId)!;
            parent.children = parent.children || [];
            parent.children.push(mappedItem);
          } else {
            rootItems.push(mappedItem);
          }
        }

        // Sortieren
        const sortItems = (items: MenuItemData[]) => {
          items.sort((a, b) => a.order - b.order);
          for (const item of items) {
            if (item.children?.length) {
              sortItems(item.children);
            }
          }
        };
        sortItems(rootItems);

        setMenuItems(rootItems);
      } catch (err) {
        console.error("Fehler beim Laden der Menü-Items:", err);
      } finally {
        setLoading(false);
      }
    };

    loadMenuItems();
  }, []);

  return { menuItems, loading };
};

export const HeaderMenuComponent = React.memo(function HeaderMenuComponent({}) {
  const t = useTranslation();
  const { tenantConfig } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();

  const { menuItems, loading } = useMenuItems();

  const [selectedKey, setSelectedKey] = React.useState<string | undefined>(
    undefined
  );
  const [openKeys, setOpenKeys] = React.useState<string[]>([]);

  // Menü-Items mit dynamischen Einträgen erweitern
  const finalMenuItems = React.useMemo(() => {
    // Startseite hinzufügen
    const items: MenuItemData[] = [
      {
        id: "home",
        key: "home",
        title: "app:menu.frontpage",
        route: "/",
        icon: "fa:home",
        order: 0,
        visible: true,
      },
    ];

    // "Inhalte" Gruppe mit dynamischen Menüpunkten
    if (menuItems.length > 0) {
      items.push({
        id: "inhalte",
        key: "inhalte",
        title: "Inhalte",
        route: "",
        icon: "fa:list",
        order: 1,
        visible: true,
        children: menuItems,
      });
    }

    // FAQ Menüpunkt
    items.push({
      id: "faq",
      key: "faq",
      title: "Fragen und Antworten",
      route: "/wasserklaerer/faq",
      icon: "fa:question-circle",
      order: 2,
      visible: true,
    });

    // Custom Menu Items aus TenantConfig
    if (
      tenantConfig?.get("menuItemLabel") &&
      tenantConfig?.get("menuItemUrl")
    ) {
      items.push({
        id: "custom1",
        key: "custom1",
        title: getTitle(tenantConfig?.get("menuItemLabel") as string),
        route: tenantConfig?.get("menuItemUrl") as string,
        icon: "fa:external-link",
        order: 100,
        visible: true,
      });
    }

    if (
      tenantConfig?.get("menuItem2Label") &&
      tenantConfig?.get("menuItem2Url")
    ) {
      items.push({
        id: "custom2",
        key: "custom2",
        title: getTitle(tenantConfig?.get("menuItem2Label") as string),
        route: tenantConfig?.get("menuItem2Url") as string,
        icon: "fa:external-link",
        order: 101,
        visible: true,
      });
    }

    if (
      tenantConfig?.get("menuItem3Label") &&
      tenantConfig?.get("menuItem3Url")
    ) {
      items.push({
        id: "custom3",
        key: "custom3",
        title: getTitle(tenantConfig?.get("menuItem3Label") as string),
        route: tenantConfig?.get("menuItem3Url") as string,
        icon: "fa:external-link",
        order: 102,
        visible: true,
      });
    }

    return items;
  }, [menuItems, tenantConfig]);

  // Selected Key basierend auf Location finden
  React.useEffect(() => {
    const findSelectedKey = (
      items: MenuItemData[],
      pathname: string
    ): string | undefined => {
      for (const item of items) {
        if (item.route && pathname === item.route) {
          return item.key;
        }
        if (
          item.route &&
          pathname.startsWith(item.route) &&
          item.route !== "/"
        ) {
          return item.key;
        }
        if (item.children?.length) {
          const found = findSelectedKey(item.children, pathname);
          if (found) return found;
        }
      }
      return undefined;
    };

    if (location.pathname === "/") {
      setSelectedKey("home");
      setOpenKeys([]);
    } else {
      const key = findSelectedKey(finalMenuItems, location.pathname);
      setSelectedKey(key);
    }
  }, [location.pathname, finalMenuItems]);

  const onClickMenu = (item: { key: string }) => {
    const findItemByKey = (
      items: MenuItemData[],
      key: string
    ): MenuItemData | null => {
      for (const menuItem of items) {
        if (menuItem.key === key) return menuItem;
        if (menuItem.children) {
          const found = findItemByKey(menuItem.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    const clicked = findItemByKey(finalMenuItems, item.key);

    if (clicked?.route) {
      const path = clicked.route;

      if (path.startsWith("http://") || path.startsWith("https://")) {
        window.open(path, "_blank")?.focus();
      } else {
        navigate(path || "/");
      }
    }
  };

  const renderMenuItem = (item: MenuItemData) => {
    const label = item.title.startsWith("app:") ? t(item.title) : item.title;

    if (item.children && item.children.length > 0) {
      return (
        <Menu.SubMenu
          key={item.key}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {item.icon && (
                <Icon style={{ fontSize: "16px" }} icon={item.icon} />
              )}
              <h4 style={{ margin: 0 }}>{label}</h4>
            </div>
          }
        >
          {item.children.map((child) => {
            if (child.children && child.children.length > 0) {
              return (
                <Menu.SubMenu
                  key={child.key}
                  title={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {child.icon && (
                        <Icon style={{ fontSize: "14px" }} icon={child.icon} />
                      )}
                      <span style={{ fontSize: "14px" }}>{child.title}</span>
                    </div>
                  }
                >
                  {child.children.map((grandChild) => (
                    <Menu.Item key={grandChild.key}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {grandChild.icon && (
                          <Icon
                            style={{ fontSize: "12px" }}
                            icon={grandChild.icon}
                          />
                        )}
                        <span style={{ fontSize: "13px" }}>
                          {grandChild.title}
                        </span>
                      </div>
                    </Menu.Item>
                  ))}
                </Menu.SubMenu>
              );
            } else {
              return (
                <Menu.Item key={child.key}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {child.icon && (
                      <Icon style={{ fontSize: "14px" }} icon={child.icon} />
                    )}
                    <span style={{ fontSize: "14px" }}>{child.title}</span>
                  </div>
                </Menu.Item>
              );
            }
          })}
        </Menu.SubMenu>
      );
    } else {
      return (
        <Menu.Item key={item.key}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {item.icon && (
              <Icon style={{ fontSize: "16px" }} icon={item.icon} />
            )}
            <h4 style={{ margin: 0 }}>{label}</h4>
          </div>
        </Menu.Item>
      );
    }
  };

  return (
    <Menu
      className="customMenu"
      triggerSubMenuAction={"click"}
      selectedKeys={selectedKey ? [selectedKey] : []}
      openKeys={openKeys}
      onOpenChange={setOpenKeys}
      mode="horizontal"
      onClick={onClickMenu}
      overflowedIndicator={
        <Icon style={{ fontSize: "20px", marginTop: "20px" }} icon="fa:bars" />
      }
    >
      {finalMenuItems.map((item) => renderMenuItem(item))}
    </Menu>
  );
});
